import { ElementRef, Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import {
  ConditionGroup,
  QueryCondition,
} from '../../../models/filter-group.model';
import {
  ALL_FIELDS,
  DA_FILTER_CRITERIA_FIELDS,
  DEFAULT_GROUP,
  EQ_STATUS_OPTIONS,
  EQ_SUBSTATUS_OPTIONS,
  FILTER_DROPDOWN_FIELDS,
  FILTER_MULTISELECT_FIELDS,
  FILTER_SERVICE_PARTNER_FIELD,
  HQ_FILTER_CRITERIA_FIELDS,
  OPERATORS_MAPPING_SYMBOL,
  RU_FILTER_CRITERIA_FIELDS,
  SERVICE_PARTNER_TYPE_OPTIONS,
  SYMBOLS_MAPPING_OPERATORS,
} from '../../constants/filter-criteria.constants';
import { SearchPartner } from '../../../models/search-partner.model';
import {
  buildFieldUiState,
  resolveFieldUiType,
} from '../logic/filter-criteria-field-ui.logic';
import {
  clearDisabledContractFieldValues,
  detectContractTypes,
  getContractUiFlags,
  isFieldDisabledByContractType,
} from '../logic/filter-criteria-contract.logic';
import {
  deduplicateOptions,
  getAllValues,
  getExcludedValues,
  getRangeValues,
  getSelectedVasTypes,
  getValueOptions,
  getVasLevelOptions,
  isMaterialNoField,
  mapBackendFieldToFrontend,
  normalizeSeparators,
  updateFilteredRanges,
} from '../logic/filter-criteria-values.logic';
import {
  scrollToConditionRow,
  syncMultiselectDropdowns,
} from '../logic/filter-criteria-dom.logic';

@Injectable({
  providedIn: 'root',
})
export class FilterCriteriaDataService {
  groups: ConditionGroup[] = [DEFAULT_GROUP];
  private nextConditionId = 2;
  filteredFields: { label: string; value: string }[] = [];
  showTextInput: boolean = true;
  showDropdownfield: boolean = false;
  showMultiselect: boolean = false; // Default to text input
  showRangeDatePicker: boolean = false;
  showDatePicker: boolean = false;
  showServicePartner: boolean = false;
  combinationErrors: { [condId: number]: string } = {};
  filteredPartnerMap: { [condId: number]: SearchPartner[] } = {};
  partnerSearchTextMap: { [condId: number]: string } = {};
  partnerSearch$ = new Subject<{ text: string; condition: QueryCondition }>();
  contractDateEnabled: boolean = true;
  contractOptions: boolean = true;
  ruContractOptions: boolean = true;
  daContractOptions: boolean = true;
  vasLevelOptions: { level: string; name: string }[] = [];
  readonly ivkSearch$ = new Subject<{ condId: string; matNos: string[] }>();
  excludedValuesMap: { [condId: number]: string } = {};
  countries: { id: string; name: string }[] = [];
  businessLines: { id: string; name: string }[] = [];
  contract: string[] = [];
  vasTypes: { id: string; name: string }[] = [];
  servicePartnerTypes: { id: string; name: string }[] = SERVICE_PARTNER_TYPE_OPTIONS;
  expandManualQuery: boolean = false;
  scrollAreaHeight: number = 372; // Dynamic height for scroll area
  ivkMap: { [condId: string]: { id: string; name: string }[] } = {};
  ivkLoadingMap: { [condId: string]: boolean } = {};
  fields = ALL_FIELDS;
  readonly eqStatusRawOptions: string[] = EQ_STATUS_OPTIONS;
  readonly eqSubstatusRawOptions: string[] = EQ_SUBSTATUS_OPTIONS;
  rangeSearchText: string = '';
  filteredRanges: string[] = [];
  includedValues: string = '';
  excludedValues: string = '';
  private readonly dropdownFields = new Set(FILTER_DROPDOWN_FIELDS);
  private readonly multiselectFields = new Set(FILTER_MULTISELECT_FIELDS);
  private readonly hqContractCriteriaFields = new Set(HQ_FILTER_CRITERIA_FIELDS);
  private readonly ruContractCriteriaFields = new Set(RU_FILTER_CRITERIA_FIELDS);
  private readonly daContractCriteriaFields = new Set(DA_FILTER_CRITERIA_FIELDS);

  constructor() {}

  generateConditionId(): number {
    return this.nextConditionId++;
  }

  /** Generates a stable, position-independent identifier for a group (used to key notes). */
  generateGroupId(): string {
    const cryptoObj = (globalThis as { crypto?: Crypto }).crypto;
    if (cryptoObj?.randomUUID) {
      return cryptoObj.randomUUID().replaceAll('-', '');
    }
    if (cryptoObj?.getRandomValues) {
      const bytes = cryptoObj.getRandomValues(new Uint8Array(16));
      return `g-${Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')}`;
    }
    throw new Error('Secure random number generation is unavailable.');
  }

  /** Assigns a stable id to any group that does not already have one. */
  ensureGroupIds(): void {
    for (const group of this.groups) {
      if (!group.id) {
        group.id = this.generateGroupId();
      }
    }
  }

  resetConditionId(seed = 1): void {
    this.nextConditionId = Math.max(1, seed);
  }

  syncNextConditionIdFromGroups(): void {
    let maxId = 0;
    for (const group of this.groups) {
      for (const condition of group.conditions) {
        maxId = Math.max(maxId, Number(condition.id) || 0);
      }
    }
    this.nextConditionId = maxId + 1;
  }

  showTextField(field: string, operator?: string) {
    this.showField(field);
    return this.showTextInput;
  }

  showDropdownField(field: string) {
    this.showField(field);
    return this.showDropdownfield;
  }

  showMultiselectField(field: string) {
    this.showField(field);
    return this.showMultiselect;
  }

  showServicePartnerField(field: string) {
    this.showField(field);
    return this.showServicePartner;
  }

  showDatePickerField(field: string) {
    this.showField(field);
    return this.showDatePicker;
  }

  showToDatePickerField(field: string, operator: string) {
    this.showField(field);
    return this.showRangeDatePicker && this.isDateRangeOperator(operator);
  }

  showField(field: string) {
    const fieldUiType = resolveFieldUiType(
      field,
      this.dropdownFields,
      this.multiselectFields,
      FILTER_SERVICE_PARTNER_FIELD,
    );

    this.applyFieldUiState(buildFieldUiState(fieldUiType, field));
  }

  private applyFieldUiState(state: {
    showTextInput: boolean;
    showDropdownfield: boolean;
    showMultiselect: boolean;
    showServicePartner: boolean;
    showDatePicker: boolean;
    showRangeDatePicker: boolean;
  }): void {
    this.showTextInput = state.showTextInput;
    this.showDropdownfield = state.showDropdownfield;
    this.showMultiselect = state.showMultiselect;
    this.showServicePartner = state.showServicePartner;
    this.showDatePicker = state.showDatePicker;
    this.showRangeDatePicker = state.showRangeDatePicker;
  }

  isDateRangeOperator(operator: string): boolean {
    const sym = this.getOperatorSymbol(operator);
    return sym === '=' || sym === '!=';
  }

  getOperatorsLabel = (operator: string): string => {
    return OPERATORS_MAPPING_SYMBOL[operator] || operator;
  };

  getOperatorSymbol = (operator: string): string => {
    return SYMBOLS_MAPPING_OPERATORS[operator] || operator;
  };

  onPartnerInput(input: any, conditions: QueryCondition): void {
    const typedInput = String(input ?? '').trim();
    this.partnerSearchTextMap[conditions.id] = typedInput;
    this.partnerSearch$.next({ text: typedInput, condition: conditions });

    if (!typedInput) {
      conditions.value = '';
      return;
    }

    // Persist free-typed partner input even when no API match is selected.
    conditions.value = typedInput;
  }

  isFieldDisabledByContractType(field: string): boolean {
    return isFieldDisabledByContractType(
      field,
      {
        contractDateEnabled: this.contractDateEnabled,
        contractOptions: this.contractOptions,
        ruContractOptions: this.ruContractOptions,
        daContractOptions: this.daContractOptions,
      },
      this.hqContractCriteriaFields,
      this.ruContractCriteriaFields,
      this.daContractCriteriaFields,
    );
  }

  updateContractOptions(): void {
    const flags = detectContractTypes(this.groups);
    const uiFlags = getContractUiFlags(flags);

    this.contractDateEnabled = uiFlags.contractDateEnabled;
    this.contractOptions = uiFlags.contractOptions;
    this.ruContractOptions = uiFlags.ruContractOptions;
    this.daContractOptions = uiFlags.daContractOptions;

    clearDisabledContractFieldValues(this.groups, (field: string) =>
      this.isFieldDisabledByContractType(field),
    );
  }

  syncMultiselectDropdowns(elementRef: ElementRef): void {
    syncMultiselectDropdowns(elementRef);
  }

  isMaterialNoField(field: string): boolean {
    return isMaterialNoField(field);
  }

  normalizeSeparators(value: string): string {
    return normalizeSeparators(value);
  }

  get eqStatusOptions(): string[] {
    return this.deduplicateOptions(this.eqStatusRawOptions).displayOptions;
  }

  get eqSubstatusOptions(): string[] {
    return this.deduplicateOptions(this.eqSubstatusRawOptions).displayOptions;
  }

  deduplicateOptions(rawOptions: string[]): {
    displayOptions: string[];
    valueMap: Record<string, string[]>;
  } {
    return deduplicateOptions(rawOptions);
  }

  getValueOptions(field: string): string[] {
    return getValueOptions(field);
  }

  /**
   * Smoothly scrolls a newly added condition row into view so the user does not
   * have to manually scroll after adding a row/child/group.
   */
  scrollToConditionRow(
    groupIndex: number,
    conditionIndex: number,
    elementRef: ElementRef,
  ): void {
    scrollToConditionRow(
      groupIndex,
      conditionIndex,
      elementRef,
      this.expandManualQuery,
    );
  }

  updateScrollAreaHeight(): void {
    // Base height + (number of groups * height per group) + (total conditions * height per condition)
    // Each group takes approximately 100px, each condition takes approximately 60-80px
    const baseHeight = 200;
    const heightPerGroup = 100;
    const heightPerCondition = 70;
    const minHeight = 372; // Minimum height (original)

    // Calculate total number of conditions across all groups
    const totalConditions = this.groups.reduce(
      (sum, group) => sum + group.conditions.length,
      0,
    );

    const calculatedHeight =
      baseHeight +
      this.groups.length * heightPerGroup +
      totalConditions * heightPerCondition;
    this.scrollAreaHeight = Math.max(calculatedHeight, minHeight);
  }

  getSelectedVasTypes(): string[] {
    return getSelectedVasTypes(this.groups);
  }

  getVasLevelOptions(
    elementRef: ElementRef,
  ): { level: string; name: string }[] {
    const selectedVasTypes = this.getSelectedVasTypes();
    const options = getVasLevelOptions(selectedVasTypes);

    if (!options.length) return [];

    setTimeout(() => {
      this.syncMultiselectDropdowns(elementRef);
    }, 50);

    return options;
  }

  updateVasLevelOptions(elementRef: ElementRef): void {
    this.vasLevelOptions = this.getVasLevelOptions(elementRef);
  }

  updateFilteredRanges(): void {
    this.filteredRanges = updateFilteredRanges(
      this.rangeSearchText,
      this.includedValues,
      this.excludedValues,
    );
  }

  getRangeValues(): string[] {
    return getRangeValues(this.includedValues);
  }

  getExcludedValues(): string[] {
    return getExcludedValues(this.excludedValues);
  }

  getAllValues(): string[] {
    return getAllValues(this.includedValues, this.excludedValues);
  }

  mapBackendFieldToFrontend(backendField: string): string {
    return mapBackendFieldToFrontend(backendField, this.fields);
  }
}
